/**
 * HTML Converter for Editor.js
 * Converts Editor.js output to email-safe HTML
 */

import type { OutputData, OutputBlockData } from '@editorjs/editorjs';
import type { ButtonBlockData } from './blocks/ButtonBlock';
import type { HeroBlockData } from './blocks/HeroBlock';
import type { ProductBlockData } from './blocks/ProductBlock';
import type { PromotionBlockData } from './blocks/PromotionBlock';
import type { FooterBlockData } from './blocks/FooterBlock';
import type { ImageBlockData } from './blocks/ImageBlock';
import type { MergeTagBlockData } from './blocks/MergeTagBlock';

// Email-safe font stack
const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Brand colors
const COLORS = {
  primary: '#D4A657',
  secondary: '#7B9D9D',
  wine: '#722f37',
  dark: '#2b2b2b',
  text: '#666666',
  lightText: '#999999',
  border: '#e5e5e5',
  background: '#f9f9f9',
};

/**
 * Convert Editor.js output to email-safe HTML
 */
export function convertToEmailHTML(data: OutputData): string {
  if (!data || !data.blocks || data.blocks.length === 0) {
    return '';
  }

  const bodyContent = data.blocks.map((block) => convertBlock(block)).join('\n');

  // Wrap in email-safe table structure
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>EJ's Wine Shop</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: #ffffff; font-family: ${FONT_STACK};">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #ffffff;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width: 600px; width: 100%;">
          <tr>
            <td style="padding: 0 20px;">
              ${bodyContent}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

/**
 * Convert a single block to HTML
 */
function convertBlock(block: OutputBlockData): string {
  switch (block.type) {
    case 'header':
      return convertHeader(block.data);
    case 'paragraph':
      return convertParagraph(block.data);
    case 'list':
      return convertList(block.data);
    case 'delimiter':
      return convertDelimiter();
    case 'button':
      return convertButton(block.data as ButtonBlockData);
    case 'hero':
      return convertHero(block.data as HeroBlockData);
    case 'product':
      return convertProduct(block.data as ProductBlockData);
    case 'promotion':
      return convertPromotion(block.data as PromotionBlockData);
    case 'footer':
      return convertFooter(block.data as FooterBlockData);
    case 'image':
    case 'emailImage':
      return convertImage(block.data as ImageBlockData);
    case 'mergeTag':
      return convertMergeTag(block.data as MergeTagBlockData);
    default:
      console.warn(`Unknown block type: ${block.type}`);
      return '';
  }
}

/**
 * Convert header block
 */
function convertHeader(data: { text: string; level: number }): string {
  const sizes: Record<number, string> = {
    1: '32px',
    2: '24px',
    3: '20px',
    4: '18px',
    5: '16px',
    6: '14px',
  };
  const size = sizes[data.level] || '24px';

  return `
    <h${data.level} style="font-size: ${size}; font-weight: 600; color: ${COLORS.dark}; margin: 0 0 16px; font-family: ${FONT_STACK}; line-height: 1.3;">
      ${data.text}
    </h${data.level}>
  `;
}

/**
 * Convert paragraph block
 */
function convertParagraph(data: { text: string }): string {
  return `
    <p style="font-size: 16px; line-height: 24px; color: ${COLORS.text}; margin: 0 0 16px; font-family: ${FONT_STACK};">
      ${data.text}
    </p>
  `;
}

/**
 * Convert list block
 * Editor.js List plugin can return items as strings OR objects with { content, items } structure
 */
function convertList(data: { style: 'ordered' | 'unordered'; items: any[] }): string {
  const tag = data.style === 'ordered' ? 'ol' : 'ul';

  const renderItems = (items: any[]): string => {
    return items
      .map((item) => {
        // Handle both string items and object items (Editor.js List v2 uses objects)
        const content = typeof item === 'string' ? item : (item.content || item.text || String(item));
        const nestedItems = item.items && item.items.length > 0
          ? `<${tag} style="margin: 8px 0 0; padding-left: 24px;">${renderItems(item.items)}</${tag}>`
          : '';

        return `
          <li style="font-size: 16px; line-height: 24px; color: ${COLORS.text}; margin: 0 0 8px; font-family: ${FONT_STACK};">
            ${content}${nestedItems}
          </li>
        `;
      })
      .join('');
  };

  return `
    <${tag} style="margin: 0 0 16px; padding-left: 24px;">
      ${renderItems(data.items)}
    </${tag}>
  `;
}

/**
 * Convert delimiter block
 */
function convertDelimiter(): string {
  return `
    <hr style="border: 0; border-top: 1px solid ${COLORS.border}; margin: 32px 0;" />
  `;
}

/**
 * Convert button block
 */
function convertButton(data: ButtonBlockData): string {
  const colors: Record<string, string> = {
    primary: COLORS.primary,
    secondary: COLORS.secondary,
    dark: COLORS.dark,
  };
  const bgColor = colors[data.style] || COLORS.primary;

  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin: 24px 0;">
      <tr>
        <td style="text-align: ${data.alignment};">
          <a href="${data.url}" style="background-color: ${bgColor}; color: #ffffff; font-size: 16px; font-weight: 600; padding: 12px 32px; border-radius: 4px; text-decoration: none; display: inline-block; font-family: ${FONT_STACK};">
            ${data.text}
          </a>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Convert hero block
 */
function convertHero(data: HeroBlockData): string {
  const bgColors: Record<string, string> = {
    wine: COLORS.wine,
    gold: COLORS.primary,
    teal: COLORS.secondary,
    dark: COLORS.dark,
  };
  const btnColors: Record<string, string> = {
    wine: COLORS.primary,
    gold: COLORS.dark,
    teal: COLORS.primary,
    dark: COLORS.primary,
  };

  const bgColor = bgColors[data.backgroundColor] || COLORS.wine;
  const btnColor = btnColors[data.backgroundColor] || COLORS.primary;

  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: ${bgColor}; border-radius: 4px; margin: 8px 0;">
      <tr>
        <td style="padding: 48px 32px; text-align: center;">
          <h1 style="font-size: 32px; font-weight: 700; line-height: 40px; color: #ffffff; margin: 0 0 12px; font-family: ${FONT_STACK};">
            ${data.headline}
          </h1>
          <p style="font-size: 18px; line-height: 28px; color: #ffffff; margin: 0 0 24px; opacity: 0.9; font-family: ${FONT_STACK};">
            ${data.subtitle}
          </p>
          <a href="${data.buttonUrl}" style="background-color: ${btnColor}; color: #ffffff; font-size: 16px; font-weight: 600; padding: 12px 32px; border-radius: 4px; text-decoration: none; display: inline-block; font-family: ${FONT_STACK};">
            ${data.buttonText}
          </a>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Convert product block
 */
function convertProduct(data: ProductBlockData): string {
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-bottom: 1px solid ${COLORS.border}; margin-bottom: 24px;">
      <tr>
        <td style="padding: 24px 0; vertical-align: top; width: 150px;">
          <img src="${data.imageUrl}" alt="${data.name}" width="150" style="border-radius: 4px; display: block;" />
        </td>
        <td style="padding: 24px 0 24px 24px; vertical-align: top;">
          <h3 style="font-size: 18px; font-weight: 600; color: ${COLORS.dark}; margin: 0 0 8px; font-family: ${FONT_STACK};">
            ${data.name}
          </h3>
          <p style="font-size: 14px; color: ${COLORS.text}; margin: 0 0 4px; font-family: ${FONT_STACK};">
            ${data.producer}
          </p>
          <p style="font-size: 14px; line-height: 22px; color: ${COLORS.text}; margin: 0 0 12px; font-family: ${FONT_STACK};">
            ${data.description}
          </p>
          <p style="font-size: 20px; font-weight: 700; color: ${COLORS.wine}; margin: 0 0 16px; font-family: ${FONT_STACK};">
            ${data.price}
          </p>
          <a href="${data.buttonUrl}" style="background-color: ${COLORS.primary}; color: #ffffff; font-size: 14px; font-weight: 600; padding: 10px 24px; border-radius: 4px; text-decoration: none; display: inline-block; font-family: ${FONT_STACK};">
            ${data.buttonText}
          </a>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Convert promotion block
 */
function convertPromotion(data: PromotionBlockData): string {
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: ${COLORS.primary}; border-radius: 8px; margin: 16px 0;">
      <tr>
        <td style="padding: 32px; text-align: center;">
          <span style="font-size: 12px; font-weight: 700; color: #ffffff; background-color: ${COLORS.wine}; padding: 4px 12px; border-radius: 12px; display: inline-block; margin: 0 0 16px; font-family: ${FONT_STACK};">
            ${data.badge}
          </span>
          <h2 style="font-size: 28px; font-weight: 700; color: ${COLORS.dark}; margin: 0 0 12px; font-family: ${FONT_STACK};">
            ${data.headline}
          </h2>
          <p style="font-size: 36px; font-weight: 700; color: ${COLORS.wine}; margin: 0 0 16px; font-family: ${FONT_STACK};">
            ${data.discount}
          </p>
          <p style="font-size: 14px; line-height: 22px; color: ${COLORS.dark}; margin: 0 0 16px; font-family: ${FONT_STACK};">
            Use code: <strong>${data.promoCode}</strong>
          </p>
          <a href="${data.buttonUrl}" style="background-color: ${COLORS.dark}; color: #ffffff; font-size: 16px; font-weight: 600; padding: 12px 32px; border-radius: 4px; text-decoration: none; display: inline-block; font-family: ${FONT_STACK};">
            ${data.buttonText}
          </a>
        </td>
      </tr>
    </table>
  `;
}

/**
 * Convert footer block
 */
function convertFooter(data: FooterBlockData): string {
  let content = `
    <p style="margin: 0 0 16px; font-size: 16px; font-weight: 600; color: ${COLORS.dark};">
      EJ's Wine Shop & Tasting Room
    </p>
  `;

  if (data.showAddress) {
    content += `
      <p style="margin: 0 0 16px; font-size: 14px; line-height: 24px; color: ${COLORS.text};">
        955 Washington St, Dubuque, IA 52001
      </p>
    `;
  }

  if (data.showHours) {
    content += `
      <p style="margin: 0 0 16px; font-size: 14px; line-height: 24px; color: ${COLORS.text};">
        <strong>Hours:</strong><br>
        Tue-Thu: 3-10pm &bull; Fri: 3-11pm &bull; Sat: 2-11pm &bull; Sun: 2-6pm &bull; Mon: Closed
      </p>
    `;
  }

  content += `
    <p style="margin: 0 0 16px; font-size: 14px; color: ${COLORS.text};">
      <a href="https://www.ejswineshop.com/contact" style="color: ${COLORS.primary}; text-decoration: none; font-weight: 500;">Contact Us</a>
      <span style="color: ${COLORS.text};"> | </span>
      <a href="https://www.ejswineshop.com/email-preferences?email={{email}}" style="color: ${COLORS.primary}; text-decoration: none; font-weight: 500;">Email Preferences</a>
    </p>
    <p style="margin: 16px 0 0; font-size: 12px; line-height: 18px; color: ${COLORS.lightText};">
      You're receiving this email because you're a valued member of EJ's Wine Shop.<br>
      <a href="https://www.ejswineshop.com/email-preferences?email={{email}}" style="color: ${COLORS.lightText}; text-decoration: underline; font-size: 12px;">Unsubscribe from this list</a>
    </p>
    <p style="margin: 16px 0 0; font-size: 12px; color: ${COLORS.lightText};">
      &copy; ${new Date().getFullYear()} EJ's Wine Shop. All rights reserved.
    </p>
  `;

  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: ${COLORS.background}; border-radius: 4px; margin-top: 24px;">
      <tr>
        <td style="padding: 32px 40px; text-align: center; font-family: ${FONT_STACK};">
          ${content}
        </td>
      </tr>
    </table>
  `;
}

/**
 * Convert image block
 */
function convertImage(data: ImageBlockData): string {
  if (!data.url) return '';

  const widths: Record<string, string> = {
    full: '100%',
    medium: '75%',
    small: '50%',
  };
  const width = widths[data.width] || '100%';

  let imageHtml = `
    <img src="${data.url}" alt="${data.alt || ''}" style="width: ${width}; max-width: 100%; height: auto; border-radius: 4px; display: block; margin: 0 auto;" />
  `;

  if (data.caption) {
    imageHtml += `
      <p style="font-size: 12px; color: ${COLORS.text}; margin: 8px 0 0; font-style: italic; text-align: center; font-family: ${FONT_STACK};">
        ${data.caption}
      </p>
    `;
  }

  return `
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin: 16px 0;">
      <tr>
        <td style="text-align: ${data.alignment || 'center'};">
          ${imageHtml}
        </td>
      </tr>
    </table>
  `;
}

/**
 * Convert merge tag block
 */
function convertMergeTag(data: MergeTagBlockData): string {
  const tagMap: Record<string, string> = {
    firstName: '{{firstName}}',
    lastName: '{{lastName}}',
    email: '{{email}}',
  };

  // Output the merge tag as inline text - it will be replaced when sending
  return `<span style="font-family: ${FONT_STACK};">${tagMap[data.tag] || '{{firstName}}'}</span>`;
}

/**
 * Extract plain text from Editor.js output (for preview/subject line)
 */
export function extractPlainText(data: OutputData): string {
  if (!data || !data.blocks) return '';

  return data.blocks
    .map((block) => {
      switch (block.type) {
        case 'header':
        case 'paragraph':
          return stripHtml(block.data.text);
        case 'list':
          return block.data.items.map((item: string) => stripHtml(item)).join(' ');
        case 'hero':
          return `${block.data.headline} ${block.data.subtitle}`;
        case 'promotion':
          return `${block.data.badge} ${block.data.headline} ${block.data.discount}`;
        case 'product':
          return `${block.data.name} - ${block.data.price}`;
        default:
          return '';
      }
    })
    .filter(Boolean)
    .join(' ')
    .slice(0, 200);
}

/**
 * Strip HTML tags from string
 */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '');
}
